import React, { useEffect, memo } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { X } from 'lucide-react';
import { Z_CLASS } from '../../constants/zIndex';

/**
 * BottomSheet - Mobile-first modal optimisé
 */
const BottomSheet = memo(function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  size = 'auto',
  showHandle = true,
  className = '',
  priority = 'normal',
}) {
  const dragControls = useDragControls();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const sizeClasses = {
    auto: 'max-h-[92svh]',
    half: 'max-h-[60svh]',
    full: 'max-h-[96svh]',
    large: 'max-h-[94svh]',
  };

  const zIndexClass = priority === 'high' ? Z_CLASS.SETTINGS_SHEET : Z_CLASS.BOTTOM_SHEET;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={`fixed inset-0 ${zIndexClass} flex items-end justify-center`}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 400 }}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 400) onClose();
            }}
            style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)' }}
            className={`
              relative w-full sm:max-w-md
              rounded-t-2xl
              ${sizeClasses[size]}
              ${className}
            `}
          >
            {/* Handle + Header */}
            <div
              className="sticky top-0 z-10 rounded-t-2xl cursor-grab active:cursor-grabbing"
              style={{ background: 'var(--bg-surface)' }}
              onPointerDown={(e) => dragControls.start(e)}
            >
              {showHandle && (
                <div className="flex justify-center py-2.5">
                  <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border-strong)' }} />
                </div>
              )}

              {title && (
                <div className="flex items-center justify-between px-4 pb-3">
                  <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {/* Content - avec safe area bottom */}
            <div className="overflow-y-auto overscroll-contain pb-safe">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
});

export { BottomSheet };
